import firebaseKit from "../firebase/firebaseKit";
import PublicChatMessage from "./PublicChatMessage";
import firebase from "firebase/app";
import "firebase/database";
import PrivateChatMessage from "./PrivateChatMessage";
import CorporateChatMessage from "./CorporateChatMessage";
import ClickStreamKit from "../ClickStreamKit";
import { refs, getRef } from "../firebase/firebaseRefKit";

export default class ChatMessageCollection {
  /**
   *
   * @param {object} me me object from UserContext
   * @param {object} messageList firebase message data
   * @param {object} urlParams contains data needed for building firebase url
   * @param {string} type sets the type of message the collection should contain, can be set to 'public', 'private' or 'corporate'
   */
  constructor({ me, urlParams, messageList, type }) {
    this.me = me;
    this.messageList = [];
    this.urlParams = urlParams;
    this.type = type;
    this.ref = this.getTypedRef();
    this._firebaseMessageList = messageList;
    this.clickStream = new ClickStreamKit();

    this._setMessageListFromFirebase(messageList);
  }

  _setMessageListFromFirebase(messageList) {
    this.messageList = this._getTypedChatMessageList(messageList);
  }

  _getClickStreamUrl() {
    switch (this.type) {
      case "public":
        return this.urlParams.pageSlug;
      case "private":
        return this.urlParams.inboxUUID;
      case "corporate":
        return this.urlParams.pageSlug;
      default:
        return "";
    }
  }

  _sendClickStream() {
    const { eventSlug } = this.urlParams;
    if (eventSlug)
      this.clickStream.createStream(eventSlug, this._getClickStreamUrl());
  }

  appendMessageToCollection({ firebaseID, payload }) {
    const message = this._getTypedChatMessage([firebaseID, payload]);
    this.messageList.push(message);
  }

  _getTypedChatMessage([firebaseID, payload], isLatestMessage) {
    const { urlParams } = this;
    let message;

    switch (this.type) {
      case "public":
        message = new PublicChatMessage({
          payload,
          urlParams: {
            ...urlParams,
            firebaseID,
          },
        });
        break;
      case "private":
        message = new PrivateChatMessage({
          payload,
          urlParams: {
            ...urlParams,
            firebaseID,
          },
        });
        break;
      case "corporate":
        message = new CorporateChatMessage({
          payload,
          urlParams: {
            ...urlParams,
            firebaseID,
          },
        });
        break;
      default:
        throw new Error("No type specified or invalid type");
    }

    message.setIsLatestMessage(isLatestMessage);

    return message;
  }

  _getTypedChatMessageList(messageList) {
    return messageList.map((messageObj, i) => {
      const isLatestMessage = i === Object.entries(messageList).length - 1;

      return this._getTypedChatMessage(messageObj, isLatestMessage);
    });
  }

  /**
   *
   * @param {string} message Chat message text
   * @param {object} options optional options (kind, hostRoomUrl, etc)
   */
  create(message = "", options = {}) {
    const { urlParams, me } = this;

    const payload = { message };

    this._sendClickStream();

    switch (this.type) {
      case "public":
        new PublicChatMessage({ me, payload, urlParams, options }).send();
        break;
      case "private":
        new PrivateChatMessage({ me, payload, urlParams, options }).send();
        break;
      case "corporate":
        new CorporateChatMessage({ me, payload, urlParams, options }).send();
        break;
      default:
        throw new Error("No type specified or invalid type");
    }
  }

  /**
   * Gets firebase ref, formatted based on ChatMessageCollection.type
   */
  getTypedRef() {
    // console.log(this.urlParams, this.type)
    switch (this.type) {
      case "public":
        return getRef(
          refs["public-messages"].$eventSlug.url.$pageSlug.messages,
          this.urlParams
        );
      case "private":
        return getRef(
          refs["private-messages"].inboxes.$inboxUUID.messages,
          this.urlParams
        );
      case "corporate":
        return getRef(
          refs["corporate-messages"].$organizationSlug.$eventSlug.inboxes
            .$pageSlug.$eventUserId.messages,
          this.urlParams
        );
      default:
        throw new Error("No type specified or invalid type");
    }
  }

  getMe() {
    return this.me;
  }

  clone() {
    const { me, urlParams, _firebaseMessageList, type } = this;

    return new ChatMessageCollection({
      me,
      urlParams,
      type,
      messageList: _firebaseMessageList,
    });
  }

  messageExists(id) {
    return this._firebaseMessageList.some((message) => {
      const [firebaseID] = message;
      return firebaseID == id;
    });
  }
}
